import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { EDITOR_TOOLS } from '@editor/config/constants'
import { EditorContext } from '@editor/contexts/Editor'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { PointArray } from '@editor/interfaces/attribute'
import { PolylineProps } from '@editor/interfaces/canvas'
import { CanvasConfig, CanvasEventsExtra } from '@editor/interfaces/editor'
import { InspectionItem, InspectionItemType } from '@editor/interfaces/inspection'
import { InspectionGroup } from '@editor/interfaces/inspectionGroup'
import { addInspectionItems, updateInspectionItems } from '@editor/pages/projects/editor/store/inspectionItems'
import {
  selectIsAdjustingLabelColors,
  selectIsAdjustingUnlabeledColors,
  selectisGroupColoring,
} from '@editor/pages/projects/editor/store/selectors'
import { patchInspectionItems, postInspectionItems } from '@editor/services/InspectionSheet'
import { pointArrayToVector3 } from '@editor/services/Points'
import { getDistanceLabel } from '@editor/services/Util'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { selectInspectionItems, useToolUtilSelector } from '@editor/store/selectors'
import { addSavingElementIds, removeSavingElementIds, setWorkingElementId } from '@editor/store/tool'
import { getContrastingColor } from '@uiw/react-color'
import { cloneDeep } from 'lodash'
import { Vector3 } from 'three'
import { useDebouncedCallback } from 'use-debounce'

import {
  CustomMeasurement,
  addCustomMeasurement,
  addViewerMeasurement,
  clearUnfinishedMeasurements,
  completeCustomMeasurement,
  completeViewerMeasurement,
  deleteCustomMeasurements,
  initCustomMeasurements,
  setViewerMeasurements,
  updateCustomMeasurements,
  updateViewerMeasurements,
} from '../store'
import {
  DISTANCE_LABEL_STYLES,
  DISTANCE_LABEL_STYLES_EDITING,
  DISTANCE_LABEL_STYLES_HOVERING,
  DISTANCE_LABEL_STYLES_WORKING,
} from '../styles'

const useMainCanvas = (): CanvasConfig => {
  // Context
  const { selectedTool, isPreviousTool } = useContext(EditorContext)
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useContext(GlobalModalContext)

  // Store
  const dispatch = useAppDispatch()
  const userType = useAppSelector((state) => state.user.userType)
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const inspectionSheet = useAppSelector((state) => state.inspectionItems.inspectionSheet)
  const inspectionItems = useAppSelector(selectInspectionItems)

  const isAdjustingUnlabeledColors = useAppSelector(selectIsAdjustingUnlabeledColors)
  const isAdjustingLabelColors = useAppSelector(selectIsAdjustingLabelColors)
  const isGroupColoring = useAppSelector(selectisGroupColoring)
  const measurementDisplaySettings = useAppSelector((state) => state.editorSettings.display.custom_measurement)
  const unlabeledColor = useAppSelector((state) => state.editorSettings.display.general.unlabeled_color)
  const currentAdjustingKey = useToolUtilSelector((state) => state.currentAdjustingKey)

  const permissionSet = useAppSelector((state) => state.editor.permissionSet)
  const inspectionGroups = useAppSelector((state) => state.inspectionGroup.groups)

  const hiddenElementIds = useToolUtilSelector((state) => state.hiddenElementIds)
  const selectedElementIds = useToolUtilSelector((state) => state.selectedElementIds)
  const hoveredElementId = useToolUtilSelector((state) => state.hoveredElementId)
  const workingElementId = useToolUtilSelector((state) => state.workingElementId)
  const editingElementId = useToolUtilSelector((state) => state.editingElementId)
  const savingElementIds = useToolUtilSelector((state) => state.savingElementIds)
  const deletingElementIds = useToolUtilSelector((state) => state.deletingElementIds)

  const customMeasurements = useAppSelector((state) => state.toolCustomMeasurement.measurements)
  const viewerMeasurements = useAppSelector((state) => state.toolCustomMeasurement.viewerMeasurements)

  // Refs
  const firstPointPlacedRef = useRef(false)
  const lastTouchPointRef = useRef<PointArray>(undefined)
  const lastAddedIdRef = useRef<string>(undefined) // to track last added measurement
  const customMeasurementsRef = useRef<CustomMeasurement[]>(customMeasurements)

  // Flags
  const isSelectedTool = selectedTool === EDITOR_TOOLS.CUSTOM_MEASUREMENT
  const isAllowedToModify = permissionSet.MODIFY.includes(userType)

  // Functions/Data to be used depending on user permssion
  const measurements = isAllowedToModify ? customMeasurements : viewerMeasurements
  const addMeasurementFn = isAllowedToModify ? addCustomMeasurement : addViewerMeasurement
  const completeMeasurementFn = isAllowedToModify ? completeCustomMeasurement : completeViewerMeasurement
  const updateMeasurementFn = isAllowedToModify ? updateCustomMeasurements : updateViewerMeasurements

  // Check if an element is being worked on
  const isSelected = useCallback((id: string) => selectedElementIds.includes(id), [selectedElementIds])
  const isHovered = useCallback((id: string) => hoveredElementId === id, [hoveredElementId])
  const isWorking = useCallback((id: string) => workingElementId === id, [workingElementId])
  const isEditing = useCallback((id: string) => editingElementId === id, [editingElementId])
  const isSaving = useCallback((id: string) => savingElementIds.includes(id), [savingElementIds])
  const isDeleting = useCallback((id: string) => deletingElementIds.includes(id), [deletingElementIds])
  const isLabelVisible = useCallback(
    (id: string) =>
      measurementDisplaySettings.primary.label.visible &&
      (isSelected(id) || isHovered(id) || isWorking(id) || !isAllowedToModify || isSelectedTool),
    [
      isSelected,
      isHovered,
      isWorking,
      isAllowedToModify,
      isSelectedTool,
      measurementDisplaySettings.primary.label.visible,
    ],
  )

  /**
   * Update customMeasurementsRef when measurements change
   */
  useEffect(() => {
    customMeasurementsRef.current = measurements
  }, [measurements])

  /**
   * Save measurement to DB
   *
   * @param measurement Measurement to save
   */
  const saveCustomMeasurement = useDebouncedCallback(async () => {
    if (!project || !inspectionArea || !inspectionSheet) return

    // If they're still adding more measurements. don't save yet
    if (firstPointPlacedRef.current) {
      // But we can't rely on the second point to be placed (they might cancel or reset it),
      // so re-run this after some time to make sure it gets saved eventually
      setTimeout(() => {
        void saveCustomMeasurement()
      }, 2000)
      return
    }

    const token = await getAccessToken()
    if (!token) return

    // Get the exact items to save, filtering out the invalid ones
    const measurementsToSave = customMeasurementsRef.current.filter(
      (measure) => !measure.inspection_item_id && measure.distanceLabel.points.length === 2,
    )

    // It could have been removed before the debounce fires
    if (!measurementsToSave.length) return

    dispatch(addSavingElementIds(measurementsToSave.map((item) => item.distanceLabel.id)))

    const items = await postInspectionItems(
      token,
      project.project_id,
      inspectionArea.inspection_area_id,
      inspectionSheet.inspection_sheet_id,
      measurementsToSave.map(({ distanceLabel: { points } }) => ({
        part_name: '', // leave empty for generic name on display until user changes it
        item_type: InspectionItemType.LengthWithDistanceTool,
        length_with_distance_tool: {
          estimated_value: pointArrayToVector3(points[0]).distanceTo(pointArrayToVector3(points[1])),
          positions_for_distance: points,
        },
      })),
      showErrorModal,
    )

    // gotta add the inspection item to the inspection items list as well
    if (items?.length) {
      dispatch(addInspectionItems(items))

      // Use the temporary ID, not the actual saved ID
      dispatch(deleteCustomMeasurements(measurementsToSave.map((item) => item.distanceLabel.id)))
      dispatch(removeSavingElementIds(measurementsToSave.map((item) => item.distanceLabel.id)))
    }
  }, 5000)

  /**
   * Update measurement to DB
   *
   * @param measurementId Measurement ID to update
   */
  const updateMeasurements = useDebouncedCallback(async () => {
    if (!project || !inspectionArea || !inspectionSheet) return

    // Find measurements that have been changed
    const getUpdatedMeasurements = (items: InspectionItem[]) =>
      customMeasurementsRef.current.filter((measurement) => {
        if (!measurement.inspection_item_id || measurement.distanceLabel.points.length < 2) return false

        const inspectionItem = items.find((item) => item.inspection_item_id === measurement.inspection_item_id)
        if (!inspectionItem || !inspectionItem.length_with_distance_tool) return false

        const existingPoints = inspectionItem.length_with_distance_tool.positions_for_distance || []
        if (!existingPoints.length) return true

        for (let i = 0; i < existingPoints.length; i += 1) {
          const existingPoint = existingPoints[i]
          const newPoint = measurement.distanceLabel.points[i]
          if (existingPoint.some((coord, index) => Math.abs(coord - newPoint[index]) > 0.0001)) {
            return true
          }
        }

        return false
      })

    // Find measurements to save by comparing points from measurement and inspection items
    const measurementsToSave = getUpdatedMeasurements(inspectionItems.items)
    if (!measurementsToSave.length) return

    const token = await getAccessToken()
    if (!token) return

    dispatch(addSavingElementIds(measurementsToSave.map((item) => item.distanceLabel.id)))

    const results = await patchInspectionItems(
      token,
      project.project_id,
      measurementsToSave.map<Partial<InspectionItem>>((measurement) => ({
        inspection_item_id: measurement.inspection_item_id,
        inspection_area_id: inspectionArea.inspection_area_id,
        inspection_sheet_id: inspectionSheet.inspection_sheet_id,
        length_with_distance_tool: {
          estimated_value: pointArrayToVector3(measurement.distanceLabel.points[0]).distanceTo(
            pointArrayToVector3(measurement.distanceLabel.points[1]),
          ),
          positions_for_distance: measurement.distanceLabel.points,
        },
      })),
      showErrorModal,
    )

    dispatch(removeSavingElementIds(measurementsToSave.map((item) => item.distanceLabel.id)))

    // gotta update the inspection item in the inspection items list as well
    if (results?.length) {
      // Get the diff again since the saved measurements might have been updated while waiting for the API
      const currentDiff = getUpdatedMeasurements(results).map((m) => m.inspection_item_id!)
      const updatedMeasurements = results.filter((item) => !currentDiff.includes(item.inspection_item_id!))
      if (updatedMeasurements.length) dispatch(updateInspectionItems(updatedMeasurements))
    }
  }, 5000)

  /**
   * Fetch measurement items from inspection items
   */
  useEffect(() => {
    dispatch(
      initCustomMeasurements(
        inspectionItems.filterByType(InspectionItemType.LengthWithDistanceTool).map<CustomMeasurement>((item) => {
          const id = item.inspection_item_id!
          return {
            ...item,
            distanceLabel: {
              id,
              points: item.length_with_distance_tool!.positions_for_distance!,
              label: {
                label: getDistanceLabel(item.length_with_distance_tool!.estimated_value!),
              },
            },
          }
        }),
      ),
    )
  }, [inspectionItems, dispatch])

  /**
   * Get distance label props, mainly for styling.
   */
  const getDistanceLabelStyles = useCallback(
    (label: CustomMeasurement, color: string) => {
      const lineBgColor = getContrastingColor(color)
      const outlineColor = lineBgColor === '#000000' ? '#888' : '#eee'
      let style: Partial<PolylineProps> = {
        ...DISTANCE_LABEL_STYLES,
        lineOutline: {
          ...DISTANCE_LABEL_STYLES.lineOutline,
          color: outlineColor,
        },
      }

      if (isHovered(label.distanceLabel.id) || isSelected(label.distanceLabel.id) || isSelectedTool) {
        style = {
          ...DISTANCE_LABEL_STYLES_HOVERING,
          lineOutline: {
            ...DISTANCE_LABEL_STYLES_HOVERING.lineOutline,
            color: outlineColor,
            thickness: 0.00065,
          },
          vertexAnchors: {
            ...DISTANCE_LABEL_STYLES_HOVERING.vertexAnchors,
            color: isSelectedTool ? '#248CE4' : DISTANCE_LABEL_STYLES.vertexAnchors?.color,
            icon: isSelectedTool ? DISTANCE_LABEL_STYLES.vertexAnchors?.icon : undefined,
            outlineOpacity: isHovered(label.distanceLabel.id) || isSelected(label.distanceLabel.id) ? 1 : 0,
            outlineColor: isHovered(label.distanceLabel.id) ? 'yellow' : '#fff',
          },
        }
      } else if (isEditing(label.distanceLabel.id)) {
        style = DISTANCE_LABEL_STYLES_EDITING
      } else if (!label?.inspection_item_id) {
        style = DISTANCE_LABEL_STYLES_WORKING
      }

      // If there's going to be a label, customize the styles a bit
      if (style.label) {
        style.label = {
          ...style.label,
          styles: {
            backgroundColor: measurementDisplaySettings.primary.label.bg_color || style.label.styles?.backgroundColor,
            color: measurementDisplaySettings.primary.label.text_color,
          },
          isAdjustingColors:
            isAdjustingLabelColors &&
            currentAdjustingKey !== undefined &&
            [`custom_measurement.primary.label.text_color`, `custom_measurement.primary.label.bg_color`].includes(
              currentAdjustingKey,
            ),
        }
      }

      return cloneDeep(style)
    },
    [
      isEditing,
      isHovered,
      isSelected,
      isSelectedTool,
      measurementDisplaySettings,
      isAdjustingLabelColors,
      currentAdjustingKey,
    ],
  )

  /**
   * Handle mouse up and touch end events.
   */
  const onMouseUp = useCallback(
    (point: PointArray, { isDragged }: CanvasEventsExtra) => {
      // If the user has been dragging the canvas, don't handle it.
      if ((!isSelectedTool && selectedTool !== EDITOR_TOOLS.MOVE) || !point || isDragged) return

      if (firstPointPlacedRef.current) {
        firstPointPlacedRef.current = false
        dispatch(completeMeasurementFn(point))
        dispatch(setWorkingElementId(undefined))

        if (isAllowedToModify) {
          void saveCustomMeasurement()
          lastAddedIdRef.current = undefined
        }
        return
      }

      lastAddedIdRef.current = crypto.randomUUID()
      const newLabel: CustomMeasurement = {
        distanceLabel: {
          id: lastAddedIdRef.current,
          points: [point],
        },
      }

      firstPointPlacedRef.current = true
      dispatch(addMeasurementFn(newLabel))
      dispatch(setWorkingElementId(lastAddedIdRef.current))
    },
    [
      selectedTool,
      isSelectedTool,
      isAllowedToModify,
      addMeasurementFn,
      completeMeasurementFn,
      saveCustomMeasurement,
      dispatch,
    ],
  )

  /**
   * When user changes to a different tool, remove working anchor.
   * Note that custom measurement has no dirty check / reset warning so
   * we cannot use that mechanism here.
   */
  useEffect(() => {
    if (isPreviousTool(EDITOR_TOOLS.CUSTOM_MEASUREMENT) && selectedTool !== EDITOR_TOOLS.FOCUS) {
      lastAddedIdRef.current = undefined
      firstPointPlacedRef.current = false
      dispatch(setWorkingElementId(undefined))
      dispatch(clearUnfinishedMeasurements())
    }
  }, [isSelectedTool, selectedTool, isPreviousTool, dispatch])

  /**
   * Clears temporary measurements on unmount.
   */
  useEffect(
    () => () => {
      dispatch(setViewerMeasurements([]))
    },
    [dispatch],
  )

  /**
   * Reset states on measurement deletion.
   */
  useEffect(() => {
    const handle = () => {
      firstPointPlacedRef.current = false
      lastAddedIdRef.current = undefined
      dispatch(setWorkingElementId(undefined))
    }

    document.addEventListener('editor:tool:customMeasurement:delete', handle)
    return () => {
      handle()
      document.removeEventListener('editor:tool:customMeasurement:delete', handle)
    }
  }, [dispatch])

  return {
    objects: {
      polylines: [
        ...useMemo(
          () =>
            // ## Existing measurements
            customMeasurements.concat(viewerMeasurements).map<PolylineProps>((label) => {
              const { inspection_item_id, inspection_group_id, distanceLabel } = label
              const { id, points } = distanceLabel
              const group = inspectionGroups.find(
                (grp) => grp.item && inspection_group_id === (grp.item as InspectionGroup).group_id,
              )
              const color = isGroupColoring ? group?.color || unlabeledColor : measurementDisplaySettings.color
              const props = getDistanceLabelStyles(label, color)
              const isCustomMeasurement = customMeasurements.some((m) => m.distanceLabel.id === id)

              return {
                ...distanceLabel,
                ...props,
                id,
                label: {
                  ...props.label,
                  label:
                    points.length === 2
                      ? getDistanceLabel(new Vector3(...points[0]).distanceTo(new Vector3(...points[1])))
                      : undefined,
                  generateLabel: (pts) =>
                    pts.length === 2 ? getDistanceLabel(new Vector3(...pts[0]).distanceTo(new Vector3(...pts[1]))) : '',
                  visible: isLabelVisible(id),
                },
                line: {
                  ...props.line,
                  color,
                },
                vertexAnchors:
                  !isDeleting(id) &&
                  (!!inspection_item_id || (!inspection_item_id && !isSaving(id))) &&
                  ((isAllowedToModify && isCustomMeasurement) || (!isAllowedToModify && !isCustomMeasurement))
                    ? {
                        ...props.vertexAnchors,
                        isInteractable: isSelectedTool,
                        onUpdated: (pts: PointArray[]) => {
                          dispatch(
                            updateMeasurementFn([{ ...label, distanceLabel: { ...distanceLabel, points: pts } }]),
                          )
                          void updateMeasurements()
                        },
                      }
                    : undefined,
                isInteractable: false,
                isWorking: isWorking(id),
                isEditing: isEditing(id),
                isAdjustingColors:
                  (isAdjustingUnlabeledColors && isGroupColoring) ||
                  (!isGroupColoring &&
                    currentAdjustingKey !== undefined &&
                    [`custom_measurement.color`].includes(currentAdjustingKey)),
                visible:
                  !hiddenElementIds.includes(label.distanceLabel.id) || hoveredElementId === label.distanceLabel.id,
              }
            }),
          [
            isAllowedToModify,
            customMeasurements,
            viewerMeasurements,
            inspectionGroups,
            isSelectedTool,
            hiddenElementIds,
            hoveredElementId,
            isGroupColoring,
            isAdjustingUnlabeledColors,
            currentAdjustingKey,
            measurementDisplaySettings.color,
            unlabeledColor,
            isLabelVisible,
            isEditing,
            isWorking,
            isSaving,
            isDeleting,
            getDistanceLabelStyles,
            updateMeasurementFn,
            updateMeasurements,
            dispatch,
          ],
        ),
      ],
    },
    events: {
      onTouchStart: (ev, point, solidMeshPoint) => {
        if (!isSelectedTool) return
        lastTouchPointRef.current = solidMeshPoint || point
      },
      onTouchEnd: (ev, extra) => {
        if (!isSelectedTool || !lastTouchPointRef.current) return
        onMouseUp(lastTouchPointRef.current, extra)
      },

      /**
       * User clicks on the canvas to confirm the anchor.
       * - If the latest distance label has only 1 anchor, just flag it. No need to do anything else
       *   as onMove will handle the rest.
       * - If the latest distance label already has 2 anchor, create a new working distance label with 1 anchor.
       *
       * @param e Mouse event
       * @param pcdPoint Clicked point in 3D Space on point cloud
       * @param solidMeshPoint Clicked point in 3D Space on solid mesh
       */
      onMouseUp(e, pcdPoint, solidMeshPoint, extra) {
        const point = pcdPoint || solidMeshPoint
        if (!isSelectedTool || !point || lastTouchPointRef.current) return
        onMouseUp(point, extra)
      },
    },
  }
}

export default useMainCanvas
